import type { AnalysisUsage } from "./analysis-usage.js";

// =============================================================================
// SHARED TYPES
// =============================================================================

export type OverallRating = "Strong" | "Solid" | "Developing" | "Needs Support";
export type ItemRating =
  | "Exceeds"
  | "Great"
  | "Meets"
  | "Not there Yet"
  | "Needs Improvement"
  | "N/A";

// Always List item evaluation
export interface AlwaysListItem {
  item: string;
  rating: ItemRating;
  evidence: string; // Specific quote or moment
  note?: string; // Brief observation
}

// Supervisor sees scorecard + coaching guidance
export interface SupervisorFeedback {
  session_type: "initial" | "follow_up";
  session_type_evidence: string;
  clinical_impression: string;

  // Always List scorecard - the PRIMARY evaluation
  always_list: AlwaysListItem[];

  // Summary
  overall_rating: OverallRating;
  strengths: string[]; // 2-4 key strengths
  growth_areas: string[]; // 0-2 focus areas

  // Coaching-specific
  coaching_notes: string; // What to be aware of when supervising
  supervision_focus: string; // ONE thing to work on in supervision

  // Flags (optional, only if concerning)
  flags?: Array<{
    concern: string;
    context: string;
  }>;
}

// RD sees detailed, actionable feedback
export interface RDFeedback {
  session_type: "initial" | "follow_up";
  clinical_impression: string;

  // Direct feedback narrative
  feedback: string; // The main feedback (celebratory or constructive)

  // Specific moments (2-4 max)
  highlights: Array<{
    moment: string; // What happened
    impact: string; // Why it mattered
    quote?: string; // Direct quote if applicable
  }>;

  // Growth opportunity (0-1, only if warranted)
  try_this?: {
    situation: string; // When this came up
    current: string; // What you said/did
    alternative: string; // What to try instead
  };

  // One focus area (optional)
  focus_area?: string;

  // Always List gaps (only items missed or poorly executed)
  always_list_gaps?: Array<{
    item: string; // Always List item name
    moment: string; // Specific moment from session
    note: string; // What to try instead
  }>;

  // Positive closing (feedback sandwich)
  encouragement?: string;
}

export type Result<T> = { success: true; data: T } | { success: false; error: string };

// =============================================================================
// ANALYSIS XML TYPES (Section-keyed data model — PRD Sections 5.2, 7.2)
// =============================================================================

export type SessionType = "initial" | "follow-up";

export type XmlItemRating =
  | "exceeds"
  | "great"
  | "meets"
  | "not_yet"
  | "needs_improvement"
  | "na"
  | "unknown";

export interface ScorecardItem {
  id: string;
  rating: XmlItemRating;
  summary: string;
  evidence?: string;
  what_to_try?: string;
  why_it_matters?: string;
  copy_paste_reinforcement?: string;
}

export interface SessionOverviewSection {
  type: SessionType;
  text: string;
}

export type RetentionStatus = "confirmed" | "unconfirmed" | "absent";

export interface SessionAction {
  category: string;
  text: string;
}

export interface SessionRetention {
  status: RetentionStatus;
  text: string;
}

export interface SessionActionsSection {
  actions: SessionAction[];
  retention?: SessionRetention;
}

export interface ScorecardSection {
  items: ScorecardItem[];
}

export interface MissedOpportunity {
  client_quote: string;
  what_happened: string;
  what_to_try: string;
  why_it_matters: string;
}

export interface MissedOpportunitiesSection {
  opportunities: MissedOpportunity[];
}

export interface ThingDoneWell {
  lede: string;
  rd_quote?: string;
  body: string;
  skill_refs: string[];
}

export interface ThingsDoneWellSection {
  observations: ThingDoneWell[];
}

export interface KeyImprovement {
  lede: string;
  where: string;
  why_it_matters: string;
  try_this: string;
}

export interface KeyImprovementsSection {
  improvements: KeyImprovement[];
}

export type RedFlagCategory = "clinical" | "counseling";
export type RedFlagSeverity = "critical" | "advisory";

export interface RedFlag {
  category: RedFlagCategory;
  severity: RedFlagSeverity;
  lede: string;
  body: string;
}

export interface RedFlagsSection {
  flags: RedFlag[];
}

export interface ErrorSectionMarker {
  error: true;
}

export type SectionValue<T> = T | ErrorSectionMarker;

export const SECTION_NAMES = [
  "session_overview",
  "session_actions",
  "always_list_scorecard",
  "counseling_skills_scorecard",
  "missed_opportunities",
  "things_done_well",
  "key_improvements",
  "red_flags",
] as const;

export type SectionName = (typeof SECTION_NAMES)[number];

export const SECTION_DISPLAY_NAMES: Record<SectionName, string> = {
  session_overview: "Session Overview",
  session_actions: "Session Actions",
  always_list_scorecard: "Always List",
  counseling_skills_scorecard: "Counseling Skills",
  missed_opportunities: "Missed Opportunities",
  things_done_well: "Things Done Well",
  key_improvements: "Key Improvements",
  red_flags: "Red Flags",
};

export function getNextIncompleteSection(
  sections: Partial<Record<SectionName, unknown>>
): SectionName | null {
  return SECTION_NAMES.find((name) => !sections[name]) ?? null;
}

export interface AnalysisSections {
  session_overview?: SectionValue<SessionOverviewSection>;
  session_actions?: SectionValue<SessionActionsSection>;
  always_list_scorecard?: SectionValue<ScorecardSection>;
  counseling_skills_scorecard?: SectionValue<ScorecardSection>;
  missed_opportunities?: SectionValue<MissedOpportunitiesSection>;
  things_done_well?: SectionValue<ThingsDoneWellSection>;
  key_improvements?: SectionValue<KeyImprovementsSection>;
  red_flags?: SectionValue<RedFlagsSection>;
}

export type AnalysisStatus = "pending" | "analyzing" | "complete" | "error";

export interface ParseWarning {
  section: SectionName;
  itemId: string;
  message: string;
}

export interface AnalysisParseResult {
  sections: AnalysisSections;
  warnings: ParseWarning[];
}

export interface AnalysisRevisionMetadata {
  id: string;
  revision: number;
  promptVersion: string;
  modelProvider: string | null;
  modelId: string | null;
  awsRegion: string | null;
  generationConfiguration: Record<string, number> | null;
  outputSchemaVersion: string | null;
  renderedPromptHash: string | null;
  userMessageHash: string | null;
  transcriptHash: string | null;
  outputHash: string | null;
  createdAt: string;
  completedAt: string | null;
  feedbackReady: boolean;
}

export interface AnalysisResponse {
  /** Present only on HTTP responses authorized for rd_usage:read. */
  usage?: AnalysisUsage | null;
  status: AnalysisStatus;
  sections: AnalysisSections;
  /** Absent only on rolling-deploy stream events from older workers. */
  revision?: AnalysisRevisionMetadata | null;
  errorMessage?: string;
  thinking?: string | null;
  hasStreamingText?: boolean;
}
